<template>
  <!-- Two drawers in one, switched at `mobileBreakpoint` (960px —
       plugins/vuetify.ts):
        - DESKTOP: permanent, optionally collapsed to the 56px icon rail.
        - MOBILE:  temporary. It overlays the content with a scrim instead of
          eating 230 of ~390 available pixels, opens from the TopBar's
          hamburger, and closes on a scrim tap, an Escape, or a navigation.
       `rail` is force-disabled on mobile (see the computed): an icon rail
       trades labels for screen width, which a temporary overlay does not
       need, and it would leave a permanent 56px stripe over the content. -->
  <v-navigation-drawer
    :model-value="mobile ? navOpen : true"
    :permanent="!mobile"
    :temporary="mobile"
    :rail="rail"
    :width="mobile ? 272 : 230"
    rail-width="56"
    :border="0"
    class="standalone-nav"
    :class="{ 'standalone-nav--mobile': mobile }"
    @update:model-value="onDrawerModel"
  >
    <!-- The brand block ("Metaship | CRM") lives in the TopBar, at the far
         left of the bar above this drawer — one identity, one place. This
         drawer carries destinations only. -->
    <div class="sh-nav" :class="{ 'sh-nav--rail': rail }">
      <div ref="scrollEl" class="sh-scroll">
        <!-- The one active-row marker for the whole nav. It is not drawn per
             row: a single element that SLIDES to the lit row (top/height
             transition) reads as one thing moving, where per-row bars would
             blink off and on. Measured, not laid out — see measureIndicator(). -->
        <div v-show="indicator" class="sh-indicator" :style="indicatorStyle" aria-hidden="true"></div>
        <!-- ── One module ⇒ the app IS that module ────────────────────────
             A tenant app with a single module has nothing to choose between:
             a parent row that expands into the only other thing on screen is
             pure ceremony, and Home is a second destination competing with
             it. So the module's sections become the sidebar itself, drawn at
             top-level metrics — identical to how legacy-client's SidebarV2
             draws a DRILLED module (`.sv2-item`: 30px rows, 13px labels, no
             child indent), not as the 12px accordion children below. -->
        <template v-if="soleModule">
          <template
            v-for="group in groupSubItems(soleModule)"
            :key="'sole-' + (group.label || '_')"
          >
            <div v-if="group.label && !rail" class="sh-section">{{ group.label }}</div>
            <div v-else-if="group.label && rail" class="sh-section__rule"></div>
            <div
              v-for="(sub, index) in group.items"
              :key="sub.route"
              class="sh-item sh-item--in"
              :class="{ 'sh-item--active': isSubActive(sub.route) }"
              :style="{ '--i': index }"
              :title="rail ? navSectionLabel(sub, soleModule.route) : undefined"
              @click="onSubClick(sub)"
            >
              <span class="sh-item__icon">
                <v-icon :size="sub.icon ? 17 : 6">{{ sub.icon || "fiber_manual_record" }}</v-icon>
              </span>
              <span v-if="!rail" class="sh-item__label">{{ navSectionLabel(sub, soleModule.route) }}</span>
            </div>
          </template>
        </template>

        <template v-else>
          <div
            v-if="homeEnabled"
            class="sh-item"
            :class="{ 'sh-item--active': isActive('/') }"
            :title="rail ? t('nav.home') : undefined"
            @click="onHomeClick"
          >
            <span class="sh-item__icon"><v-icon size="17">home</v-icon></span>
            <span v-if="!rail" class="sh-item__label">{{ t("nav.home") }}</span>
          </div>

          <template v-for="item in navItems" :key="item.route">
            <!-- Rail: the row is a hover flyout activator, so a collapsed sidebar
                 still reaches a module's sections (SidebarV2's rail contract). -->
            <v-menu
              v-if="rail && hasItems(item)"
              open-on-hover
              location="right"
              transition="slide-x-transition"
              :open-delay="50"
              :close-delay="60"
            >
              <template #activator="{ props }">
                <div
                  class="sh-item"
                  :class="{ 'sh-item--active': isActive(item.route) }"
                  v-bind="props"
                  @click="onItemClick(item)"
                >
                  <span class="sh-item__icon">
                    <v-icon size="17">{{ item.icon || "circle" }}</v-icon>
                  </span>
                  <v-icon size="11" class="sh-item__more">chevron_right</v-icon>
                </div>
              </template>
              <div class="sh-flyout">
                <div class="sh-flyout__title">{{ navLabel(item) }}</div>
                <div
                  v-for="(sub, index) in item.items"
                  :key="sub.route"
                  class="sh-child"
                  :class="{ 'sh-child--active': isSubActive(sub.route) }"
                  :style="{ '--i': index }"
                  @click="onSubClick(sub)"
                >
                  <v-icon :size="sub.icon ? 13 : 6" class="sh-child__bullet">
                    {{ sub.icon || "fiber_manual_record" }}
                  </v-icon>
                  <span class="sh-child__label">{{ navSectionLabel(sub, item.route) }}</span>
                </div>
              </div>
            </v-menu>

            <div
              v-else
              class="sh-item"
              :class="{ 'sh-item--active': isActive(item.route) }"
              :title="rail ? navLabel(item) : undefined"
              @click="onItemClick(item)"
            >
              <span class="sh-item__icon">
                <v-icon size="17">{{ item.icon || "circle" }}</v-icon>
              </span>
              <span v-if="!rail" class="sh-item__label">{{ navLabel(item) }}</span>
              <v-icon
                v-if="!rail && hasItems(item)"
                size="14"
                class="sh-item__chev"
                :class="{ 'sh-item__chev--open': expanded === item.route }"
                >expand_more</v-icon
              >
            </div>

            <!-- Expanded sub-sections. Grouped by the sub-item's own `section`
                 eyebrow, exactly as SidebarV2 renders a drilled module. -->
            <!-- Height is animated by the JS hooks (auto → px → auto): CSS
                 cannot transition to `height: auto`, and a section list that
                 pops open in one frame is what makes a nav feel plain. -->
            <Transition name="sh-expand" @enter="onExpandEnter" @after-enter="onExpandDone" @leave="onExpandLeave">
              <div v-if="!rail && hasItems(item) && expanded === item.route" class="sh-children">
                <template v-for="group in groupSubItems(item)" :key="item.route + '-' + (group.label || '_')">
                  <div v-if="group.label" class="sh-children__label">{{ group.label }}</div>
                  <div
                    v-for="(sub, index) in group.items"
                    :key="sub.route"
                    class="sh-child"
                    :class="{ 'sh-child--active': isSubActive(sub.route) }"
                    :style="{ '--i': index }"
                    @click="onSubClick(sub)"
                  >
                    <v-icon :size="sub.icon ? 14 : 6" class="sh-child__bullet">
                      {{ sub.icon || "fiber_manual_record" }}
                    </v-icon>
                    <span class="sh-child__label">{{ navSectionLabel(sub, item.route) }}</span>
                  </div>
                </template>
              </div>
            </Transition>
          </template>
        </template>
      </div>

      <!-- Organisation · user · theme — pinned under the last destination
           (DEV-923). Lives in its own component so this file stays about
           destinations. -->
      <SidebarFooter :rail="rail" />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { navigateToUrl } from "single-spa";
import { useSessionStore } from "@/stores/session";
import { closeNav, navOpen, rail as railPreference } from "./navState";
import SidebarFooter from "./SidebarFooter.vue";
import type { NavBarItem, NavBarSubItem } from "@/tenant/types";

interface SubItemGroup {
  label: string | null;
  items: NavBarSubItem[];
}

/** Accordion open/close duration — keep in step with `.sh-expand-*-active` below. */
const EXPAND_MS = 260;

export default defineComponent({
  name: "Sidebar",
  components: { SidebarFooter },
  setup() {
    const { t, te } = useI18n();
    const session = useSessionStore();
    // `mobileBreakpoint: 960` — plugins/vuetify.ts.
    const { mobile } = useDisplay();
    const path = ref(window.location.pathname + window.location.search);

    /** The icon rail is a desktop-only affordance — see the template comment. */
    const rail = computed(() => !mobile.value && railPreference.value);

    /**
     * Vuetify writes `false` back through here when a temporary drawer is
     * dismissed by its scrim, by Escape, or by a swipe. Without it the drawer
     * would reopen on the next render, because `model-value` is a one-way
     * binding off `navOpen`.
     */
    function onDrawerModel(open: boolean): void {
      if (mobile.value) navOpen.value = open;
    }

    window.addEventListener("single-spa:before-routing-event", () => {
      path.value = window.location.pathname + window.location.search;
    });
    window.addEventListener("popstate", () => {
      path.value = window.location.pathname + window.location.search;
    });

    const navItems = computed(() => session.navItems);

    function hasItems(item: NavBarItem | null): boolean {
      return !!(item && item.items && item.items.length);
    }

    /**
     * The app's ONE module, when it has exactly one and that module has
     * sections. Its sections replace the whole nav: no parent row, and no
     * Home tab above them (see the template).
     */
    const soleModule = computed<NavBarItem | null>(() => {
      const items = navItems.value;
      const only = items.length === 1 ? (items[0] as NavBarItem) : null;
      return hasItems(only) ? only : null;
    });

    const homeEnabled = computed(() => !!session.tenant?.homePage?.enabled && !soleModule.value);

    /**
     * A route WITH a `?section=` (a specific sub-item, or a module collapsed
     * to a single allowed item) is active only on an EXACT path+section
     * match — otherwise every sub-item under the same bare path lit up
     * together the instant the user was anywhere in the module. A route
     * with NO section (a parent/default row) keeps matching its own path
     * plus any nested sub-path.
     */
    function isActive(route: string): boolean {
      const [routePath, routeQuery] = route.split("?");
      const routeSection = new URLSearchParams(routeQuery || "").get("section");
      const [currentPath, currentQuery] = path.value.split("?");
      const currentSection = new URLSearchParams(currentQuery || "").get("section");

      if (routeSection != null) {
        return currentPath === routePath && currentSection === routeSection;
      }
      return currentPath === routePath || (currentPath ?? "").startsWith(`${routePath}/`);
    }

    /**
     * A SUB-item matches on path AND section, with "no section" being a value
     * of its own — the module's default section (e.g. CRM's "Overview", route
     * `/crm`) must not stay lit while the visitor is on `/crm?section=deals`.
     * `isActive` deliberately keeps the loose prefix match for the PARENT row,
     * which represents the whole module. Same rule as
     * `nav/routeGuard.ts:decideRouteGuard`.
     */
    function isSubActive(route: string): boolean {
      const [routePath, routeQuery] = route.split("?");
      const [currentPath, currentQuery] = path.value.split("?");
      return (
        routePath === currentPath &&
        new URLSearchParams(routeQuery || "").get("section") ===
          new URLSearchParams(currentQuery || "").get("section")
      );
    }

    /** The module the visitor is currently inside, if it has sections to show. */
    const activeParentRoute = computed<string | null>(() => {
      const hit = navItems.value.find((item) => hasItems(item) && isActive(item.route.split("?")[0] as string));
      return hit ? hit.route : null;
    });

    /**
     * `undefined` = follow the route (the module you are IN shows its sections
     * without a click — a tenant app has a handful of modules, so a sidebar
     * that hides every destination behind an accordion is mostly empty space).
     * A string/null = the visitor overrode that by clicking a parent row; the
     * override is dropped again on the next navigation.
     */
    const manualExpanded = ref<string | null | undefined>(undefined);
    const expanded = computed(() =>
      manualExpanded.value === undefined ? activeParentRoute.value : manualExpanded.value,
    );
    watch(path, () => {
      manualExpanded.value = undefined;
    });

    /** Consecutive sub-items sharing a `section` become one eyebrow-labelled group. */
    function groupSubItems(item: NavBarItem | null): SubItemGroup[] {
      const groups: SubItemGroup[] = [];
      for (const sub of item?.items || []) {
        const label = sub.section || null;
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(sub);
        else groups.push({ label, items: [sub] });
      }
      return groups;
    }

    // ── Sliding active marker ─────────────────────────────────────────
    // The lit row is found in the DOM after each render rather than derived
    // from the route: the same route may light a top-level row, an accordion
    // child, or a promoted sole-module row, and only the DOM knows where that
    // row ended up once the accordion has opened. A child wins over its
    // parent — the marker sits on the deepest lit row.
    const scrollEl = ref<HTMLElement | null>(null);
    const indicator = ref<{ top: number; height: number } | null>(null);
    const indicatorStyle = computed(() =>
      indicator.value ? { top: `${indicator.value.top}px`, height: `${indicator.value.height}px` } : undefined,
    );

    function measureIndicator(): void {
      const root = scrollEl.value;
      if (!root) return;
      const lit =
        root.querySelector<HTMLElement>(".sh-child--active") ?? root.querySelector<HTMLElement>(".sh-item--active");
      indicator.value = lit ? { top: lit.offsetTop, height: lit.offsetHeight } : null;
    }
    watch([path, expanded, rail, navItems, mobile], () => void nextTick(measureIndicator), { flush: "post" });
    // The accordion's height animation moves every row below it while it
    // runs — keep the marker glued to its row through the whole motion.
    let expandTimer: ReturnType<typeof setInterval> | null = null;
    function trackDuringExpand(): void {
      if (expandTimer) clearInterval(expandTimer);
      const startedAt = Date.now();
      expandTimer = setInterval(() => {
        measureIndicator();
        if (Date.now() - startedAt > EXPAND_MS + 40 && expandTimer) {
          clearInterval(expandTimer);
          expandTimer = null;
        }
      }, 16);
    }
    onMounted(() => void nextTick(measureIndicator));
    onBeforeUnmount(() => {
      if (expandTimer) clearInterval(expandTimer);
    });

    // ── Accordion height (auto → px → auto) ───────────────────────────
    function onExpandEnter(el: Element): void {
      const node = el as HTMLElement;
      node.style.height = "0px";
      node.style.overflow = "hidden";
      void node.offsetHeight; // commit the 0 before the target height, or nothing transitions
      node.style.height = `${node.scrollHeight}px`;
      trackDuringExpand();
    }
    function onExpandDone(el: Element): void {
      const node = el as HTMLElement;
      node.style.height = "";
      node.style.overflow = "";
      measureIndicator();
    }
    function onExpandLeave(el: Element): void {
      const node = el as HTMLElement;
      node.style.height = `${node.scrollHeight}px`;
      node.style.overflow = "hidden";
      void node.offsetHeight;
      node.style.height = "0px";
      trackDuringExpand();
    }

    function onHomeClick(): void {
      navigateToUrl("/");
      closeNav();
    }

    function onItemClick(item: NavBarItem): void {
      if (hasItems(item)) {
        manualExpanded.value = expanded.value === item.route ? null : item.route;
        // On mobile a navigation closes the drawer, so a parent row that both
        // navigated AND closed would make its own sections unreachable — the
        // sections would flash and vanish with the drawer. There, the parent
        // row expands and nothing else; the sub-item does the travelling.
        if (mobile.value) return;
      }
      navigateToUrl(item.route);
      closeNav();
    }

    // DEV-707 / SidebarV2: translate host nav by stable route, fall back to
    // the payload name so a custom org entry never renders a raw key.
    function sectionIdFor(route: string): string {
      return new URLSearchParams(route.split("?")[1] || "").get("section") || "default";
    }
    function modulePath(route: string): string {
      return route.split("?")[0] || route;
    }
    function navLabel(item: NavBarItem | null): string {
      if (!item) return "";
      const key = item.route ? `nav.modules.${modulePath(item.route)}` : "";
      return key && te(key) ? t(key) : item.name || "";
    }
    function navSectionLabel(sub: NavBarSubItem | null, moduleRoute: string | undefined): string {
      if (!sub || !moduleRoute) return sub?.name || "";
      const key = `nav.sections.${modulePath(moduleRoute)}.${sectionIdFor(sub.route)}`;
      return te(key) ? t(key) : sub.name || "";
    }

    function onSubClick(sub: NavBarSubItem): void {
      const samePath = path.value.split("?")[0] === sub.route.split("?")[0];
      navigateToUrl(sub.route);
      closeNav();
      if (samePath) {
        const [p, q] = sub.route.split("?");
        window.dispatchEvent(
          new CustomEvent("metaship:navigate", { detail: { path: p, query: Object.fromEntries(new URLSearchParams(q || "")) } }),
        );
      }
    }

    return {
      t,
      navLabel,
      navSectionLabel,
      navItems,
      soleModule,
      homeEnabled,
      mobile,
      navOpen,
      rail,
      onDrawerModel,
      expanded,
      hasItems,
      isActive,
      isSubActive,
      groupSubItems,
      onHomeClick,
      onItemClick,
      onSubClick,
      scrollEl,
      indicator,
      indicatorStyle,
      onExpandEnter,
      onExpandDone,
      onExpandLeave,
    };
  },
});
</script>

<style scoped>
/* Density, radii and hover strengths mirror legacy-client's SidebarV2.vue
   (`.sv2-*`) — see src/styles/standalone.css for the shared tokens. Sit BELOW
   the fixed TopBar rather than under it: Vuetify's own layout registration
   handles this inside THIS <v-app>, but it is forced explicitly so the drawer's
   position matches the SAME --metaship-header-height contract the hosted
   single-spa modules read for their own padding-top, rather than depending on
   two different mechanisms staying in sync by accident. */
.standalone-nav {
  top: var(--metaship-header-height, 0px) !important;
  height: calc(100% - var(--metaship-header-height, 0px)) !important;
  background: var(--sh-chrome-bg) !important;
  border-right: 1px solid var(--sh-hairline) !important;
}
/* Overlay, not layout: on mobile the drawer floats above the module with a
   shadow instead of a shared border. Vuetify draws its own scrim (a sibling
   of this element, so it is not styled from here); a tap on it closes the
   drawer. */
.standalone-nav--mobile {
  border-right: 0 !important;
  box-shadow: 0 20px 48px -12px rgba(0, 0, 0, 0.55) !important;
}

.sh-nav {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 8px;
  overflow: hidden;
}
.sh-nav--rail {
  padding: 10px 4px;
}

/* ── Scroll area ───────────────────────────────────────────────────────── */
.sh-scroll {
  position: relative; /* the offsetParent the sliding marker is measured against */
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 -4px;
  padding: 0 4px;
}

/* ── Sliding active marker ─────────────────────────────────────────────
   A 3px brand bar with a soft glow, on the row's left edge. `top`/`height`
   are set inline by measureIndicator(); the transition is what makes a
   route change read as the marker TRAVELLING to the new row. */
.sh-indicator {
  position: absolute;
  left: 4px;
  width: 3px;
  border-radius: 3px;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 0 10px 1px rgba(var(--v-theme-primary), 0.55);
  pointer-events: none;
  z-index: 1;
  transition:
    top var(--sh-motion-slow) var(--sh-ease),
    height var(--sh-motion-slow) var(--sh-ease),
    opacity var(--sh-motion) ease;
}
.sh-nav--rail .sh-indicator {
  left: 0;
}

/* ── Nav rows ──────────────────────────────────────────────────────────── */
.sh-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  height: var(--sh-row-height);
  padding: 0 8px;
  margin: 1px 0;
  border-radius: var(--sh-radius);
  cursor: pointer;
  user-select: none;
  transition:
    background var(--sh-motion) ease,
    transform var(--sh-motion) var(--sh-ease);
}
.sh-nav--rail .sh-item {
  justify-content: center;
  padding: 0;
  height: 34px;
}
.sh-item:hover {
  background: var(--sh-hover);
}
/* A press gives back a little: the row dips, the way a real key does. */
.sh-item:active {
  transform: scale(0.985);
}
.sh-item--active {
  background: var(--sh-active);
}
/* Rows of a freshly-drawn list arrive one after another, top to bottom
   (`--i` is the row's index, set inline). Plays on mount only — a route
   change that keeps the same rows does not replay it. */
.sh-item--in {
  /* `backwards`, not `both`: a forwards-filled `transform: none` would outrank
     the :active press-dip below for the row's whole life. */
  animation: sh-row-in var(--sh-motion-slow) var(--sh-ease) backwards;
  animation-delay: calc(var(--i, 0) * 24ms);
}
.sh-item__icon {
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: var(--sh-text-60);
  transition:
    color var(--sh-motion) ease,
    transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-item:hover .sh-item__icon {
  color: var(--sh-text);
  transform: translateX(1px) scale(1.08);
}
.sh-item--active .sh-item__icon {
  color: rgb(var(--v-theme-primary));
  animation: sh-icon-pop var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-item__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color var(--sh-motion) ease;
  /* Labels are `v-if`-ed away in the rail, so they MOUNT when it expands —
     this is the rail-expand reveal, with no extra state. */
  animation: sh-label-in var(--sh-motion-slow) var(--sh-ease) both;
}
.sh-item--active .sh-item__label {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}
.sh-item__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
  transition:
    transform var(--sh-motion-slow) var(--sh-ease-spring),
    color var(--sh-motion) ease;
}
.sh-item:hover .sh-item__chev {
  color: var(--sh-text) !important;
}
.sh-item__chev--open {
  transform: rotate(180deg);
}
/* Rail: a corner cue that the row hides more behind a flyout. */
.sh-item__more {
  position: absolute;
  right: 2px;
  bottom: 3px;
  color: var(--sh-text-45) !important;
}

/* ── Section eyebrow (single-module nav) ───────────────────────────────
   Same type and pitch as SidebarV2's `.sv2-section__header`: it labels the
   group BELOW it, so the gap sits above. */
.sh-section {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  margin-top: 5px;
  padding: 0 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
  user-select: none;
  animation: sh-label-in var(--sh-motion-slow) var(--sh-ease) both;
}
/* The eyebrow trails off into a hairline — a heading, not a floating word. */
.sh-section::after {
  content: "";
  flex: 1 1 auto;
  height: 1px;
  background: linear-gradient(90deg, var(--sh-hairline), transparent);
}
/* The rail has no room for the label, so the group boundary becomes a rule. */
.sh-section__rule {
  height: 1px;
  margin: 6px 8px;
  background: var(--sh-hairline);
}

/* ── Sub-sections (accordion, multi-module nav) ────────────────────────
   Metrics mirror SidebarV2's `.sv2-children__label` / `.sv2-child`. */
.sh-children {
  padding: 1px 0 3px;
}
.sh-expand-enter-active,
.sh-expand-leave-active {
  transition:
    height 260ms var(--sh-ease),
    opacity var(--sh-motion) ease;
}
.sh-expand-enter-from,
.sh-expand-leave-to {
  opacity: 0;
}
.sh-children__label {
  padding: 4px 8px 2px 30px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--sh-text-45);
}
.sh-child {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 26px;
  padding: 0 8px 0 30px;
  border-radius: var(--sh-radius);
  cursor: pointer;
  transition:
    background var(--sh-motion) ease,
    transform var(--sh-motion) var(--sh-ease);
  animation: sh-row-in var(--sh-motion-slow) var(--sh-ease) backwards;
  animation-delay: calc(var(--i, 0) * 28ms);
}
.sh-child:hover {
  background: var(--sh-hover);
}
.sh-child:active {
  transform: scale(0.985);
}
.sh-child--active {
  background: var(--sh-active);
}
.sh-child__bullet {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
  transition:
    color var(--sh-motion) ease,
    transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-child:hover .sh-child__bullet {
  color: var(--sh-text) !important;
  transform: scale(1.15);
}
.sh-child--active .sh-child__bullet {
  color: rgb(var(--v-theme-primary)) !important;
}
.sh-child__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-child--active .sh-child__label {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* ── Rail flyout ───────────────────────────────────────────────────────── */
.sh-flyout {
  min-width: 190px;
  padding: 6px;
  border-radius: 10px;
  background: rgb(var(--v-theme-card));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 14px 34px -18px rgba(0, 0, 0, 0.6);
}
.sh-flyout__title {
  padding: 4px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
}
.sh-flyout .sh-child {
  padding-left: 8px;
}

/* ── Motion ────────────────────────────────────────────────────────────
   Every keyframe here is namespaced `sh-` (module CSS is global on the same
   page). `prefers-reduced-motion` switches them all off in standalone.css. */
@keyframes sh-row-in {
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sh-label-in {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sh-icon-pop {
  0% {
    transform: scale(0.8);
  }
  60% {
    transform: scale(1.18);
  }
  100% {
    transform: scale(1);
  }
}

/* ── Touch targets ─────────────────────────────────────────────────────
   LAST in the sheet on purpose: a media query does not raise specificity,
   so these must come after the desktop rules they override. `959.98px`, not
   `960px`, to match Vuetify's own `width < mobileBreakpoint` test exactly —
   at 960 the drawer is still permanent. The desktop rows are 30px, which is
   right for a mouse and under the 44px minimum for a finger. */
@media (max-width: 959.98px) {
  .sh-item {
    height: 44px;
  }
  .sh-item__label {
    font-size: 14px;
  }
  .sh-child {
    height: 40px;
    padding-left: 34px;
  }
  .sh-child__label {
    font-size: 13px;
  }
  .sh-section {
    height: 26px;
  }
}
</style>
